import { Component, computed, effect, inject, input, output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CtaSelectService } from '@campaign-form/form-fields/creators-and-ctas/cta-select.service';
import { DeliverableConfigurationStore } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/deliverable-configuration.store';
import { IDeliverableConfigurationApi } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/deliverable-list.interface';
import { EDeliverableType, EPlatformFeature } from '@enums/campaign.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatErrorComponent } from '@shared/components/mat-error/mat-error.component';
import { SocialBadgeComponent } from '@shared/components/social-badge/social-badge.component';
import { camelCase } from 'lodash';
import { IBriefStepState } from '../brief-step.interface';
import { IBriefPlatformValue } from './platform-step.interface';

export type { IBriefPlatformValue } from './platform-step.interface';

// Same platform-picker UI as the real deliverable-flow-dialog's platform-step.component.html
// (grid of platform-step-card buttons with a social badge) minus the parts that need a
// persisted campaign - the "add platform" input, global template search, creator-availability
// filtering. Every platform is offered here since there's no persisted campaign/creators yet to
// filter against.
@Component({
  imports: [MatTooltipModule, SocialBadgeComponent, TranslateModule, MatErrorComponent],
  providers: [DeliverableConfigurationStore],
  selector: 'app-brief-platform-step',
  styleUrls: ['./platform-step.component.scss'],
  templateUrl: './platform-step.component.html',
})
export class BriefPlatformStepComponent {
  public readonly initialValue = input.required<IBriefPlatformValue>();
  // The current template's deliverable types, so each platform option can warn about the ones it
  // doesn't support - matches CtaTemplatePlatformChangeService.getAvailableDeliverableTypes'
  // camelCase-comparison approach, applied here per-option instead of for a single target.
  public readonly existingDeliverableTypes = input<EDeliverableType[]>([]);
  public readonly stateChange = output<IBriefStepState<IBriefPlatformValue>>();

  public readonly options = Object.values(CtaSelectService.allCtaOptions);

  public readonly form = new FormGroup({
    platformFeature: new FormControl<EPlatformFeature | null>(null, Validators.required),
  });

  private readonly deliverableConfigurationStore = inject(DeliverableConfigurationStore);
  private readonly translate = inject(TranslateService);

  public readonly unsupportedTooltipByPlatformFeature = computed<Partial<Record<EPlatformFeature, string>>>(() => {
    const configurations = this.deliverableConfigurationStore.configurations$();
    const existingTypes = this.existingDeliverableTypes();

    if (!configurations || !existingTypes.length) {
      return {};
    }

    return this.options.reduce((accumulator: Partial<Record<EPlatformFeature, string>>, option) => {
      const unsupportedTypes = this.getUnsupportedTypes(configurations, option.platformFeature, existingTypes);

      if (!unsupportedTypes.length) {
        return accumulator;
      }

      const deliverableNames = unsupportedTypes
        .map((type) => this.translate.instant(`shared.deliverableTypes.${type}`))
        .join(', ');

      accumulator[option.platformFeature] = this.translate.instant(
        'dialogs.createCampaignDialog.briefWizard.platformStep.unsupportedDeliverables',
        { deliverables: deliverableNames },
      );

      return accumulator;
    }, {});
  });

  constructor() {
    this.deliverableConfigurationStore.fetchAvailableConfigurations();

    effect(() => {
      this.form.patchValue(this.initialValue(), { emitEvent: false });
      this.form.markAllAsTouched();
      this.emitState();
    });

    this.form.valueChanges.subscribe(() => this.emitState());
  }

  public onPlatformSelected(platformFeature: EPlatformFeature): void {
    this.form.controls.platformFeature.setValue(platformFeature);
  }

  private getUnsupportedTypes(
    configurations: IDeliverableConfigurationApi[],
    platformFeature: EPlatformFeature,
    existingTypes: EDeliverableType[],
  ): EDeliverableType[] {
    const availableTypes = new Set(
      configurations
        .filter(
          (configuration) =>
            configuration.active && camelCase(configuration.platformFeature) === camelCase(platformFeature),
        )
        // The API's `deliverable` field is typed as EDeliverableType but arrives as a raw
        // PascalCase-ish string (see CtaTemplatePlatformChangeService.getNormalizedDeliverableType,
        // which does the same normalization for the equivalent target-platform check).
        .map((configuration) => camelCase(configuration.deliverable) as EDeliverableType),
    );

    return existingTypes.filter((type) => !availableTypes.has(type));
  }

  private emitState(): void {
    this.stateChange.emit({ valid: this.form.valid, value: this.form.getRawValue() });
  }
}
